<template>
  <div class="sponsor-page">
    <MenuBar />
    <div class="content">
      <div class="page-header">
        <h1>Sponsor Management</h1>
        <p>Manage sponsor profile details and campaign tracker entries in one place.</p>
      </div>

      <div class="section sponsor-section accordion-card" :class="{ open: openPanels.sponsorCreate }">
        <button class="section-toggle" type="button" @click="togglePanel('sponsorCreate')">
          <div class="section-header">
            <div>
              <h2>1. Sponsor Create</h2>
              <p class="section-subtitle">Create and update sponsor profile information.</p>
            </div>
            <div class="section-meta">
              <span class="toggle-icon" :class="{ rotated: openPanels.sponsorCreate }">⌄</span>
            </div>
          </div>
        </button>

        <div v-show="openPanels.sponsorCreate" class="section-body">
        <form class="grid sponsor-form form-shell" @submit.prevent="saveSponsor">
          <div class="group-title field-wide">Sponsor Profile</div>
          <label class="field">
            <span>Sponsor Name</span>
            <input v-model.trim="sponsorForm.sponsorName" placeholder="Enter sponsor name" required />
          </label>
          <label class="field">
            <span>Sponsor Logo URL</span>
            <input v-model.trim="sponsorForm.sponsorLogo" placeholder="https://..." />
          </label>
          <label class="field field-wide">
            <span>Sponsor Address</span>
            <input v-model.trim="sponsorForm.sponsorAddress" placeholder="Office address" />
          </label>
          <div class="group-title field-wide">Contact Information</div>
          <label class="field">
            <span>Office Number</span>
            <input v-model.trim="sponsorForm.officeNumber" placeholder="Office number" />
          </label>
          <label class="field">
            <span>Contact Person 1</span>
            <input v-model.trim="sponsorForm.contactPerson1" placeholder="Primary contact" />
          </label>
          <label class="field">
            <span>Contact Number 1</span>
            <input v-model.trim="sponsorForm.contactNumber1" placeholder="Primary number" />
          </label>
          <label class="field">
            <span>Contact Person 2</span>
            <input v-model.trim="sponsorForm.contactPerson2" placeholder="Secondary contact" />
          </label>
          <label class="field">
            <span>Contact Number 2</span>
            <input v-model.trim="sponsorForm.contactNumber2" placeholder="Secondary number" />
          </label>
          <div class="group-title field-wide">Tracking Details</div>
          <label class="field">
            <span>Onboard Date</span>
            <input v-model="sponsorForm.onboardDate" type="date" />
          </label>
          <label class="field">
            <span>Sponsor Level</span>
            <input v-model.trim="sponsorForm.sponsorLevel" placeholder="Level" />
          </label>
          <label class="field">
            <span>Status</span>
            <select v-model="sponsorForm.status">
              <option value="good">Good</option>
              <option value="payment_delay">Payment Delay</option>
              <option value="due">Due</option>
              <option value="hold">Hold</option>
            </select>
          </label>
          <label class="field field-wide">
            <span>Previous Note</span>
            <textarea v-model.trim="sponsorForm.previousNote" placeholder="Previous note"></textarea>
          </label>
          <div class="action-row field-wide">
            <button class="btn" type="submit">{{ sponsorForm.id ? 'Update Sponsor' : 'Create Sponsor' }}</button>
            <button v-if="sponsorForm.id" class="btn secondary" type="button" @click="resetSponsorForm">Cancel Edit</button>
          </div>
        </form>
        </div>
      </div>

      <div class="section sponsor-list-section accordion-card" :class="{ open: openPanels.sponsorList }">
        <button class="section-toggle" type="button" @click="togglePanel('sponsorList')">
          <div class="section-header">
            <div>
              <h2>2. Sponsor List</h2>
              <p class="section-subtitle">Browse sponsors and select one for campaign management.</p>
            </div>
            <div class="section-meta">
              <span class="count-pill">{{ sponsors.length }} Sponsors</span>
              <span class="toggle-icon" :class="{ rotated: openPanels.sponsorList }">⌄</span>
            </div>
          </div>
        </button>

        <div v-show="openPanels.sponsorList" class="section-body">
        <div class="filters-shell">
          <label class="field">
            <span>Search Name</span>
            <input v-model.trim="sponsorFilters.name" placeholder="Search sponsor name" />
          </label>
          <label class="field">
            <span>Status</span>
            <select v-model="sponsorFilters.status">
              <option value="">All</option>
              <option value="good">Good</option>
              <option value="payment_delay">Payment Delay</option>
              <option value="due">Due</option>
              <option value="hold">Hold</option>
            </select>
          </label>
          <label class="field">
            <span>Onboard From</span>
            <input v-model="sponsorFilters.onboardFrom" type="date" />
          </label>
          <label class="field">
            <span>Onboard To</span>
            <input v-model="sponsorFilters.onboardTo" type="date" />
          </label>
          <div class="filter-actions">
            <button class="btn secondary small" type="button" @click="clearSponsorFilters">Clear</button>
          </div>
        </div>

        <div class="table-wrap" v-if="sponsors.length">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Level</th>
                <th>Status</th>
                <th>Onboard Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="sponsor in sponsors" :key="sponsor.id">
                <td class="strong">{{ sponsor.sponsorName }}</td>
                <td>{{ sponsor.sponsorLevel || '-' }}</td>
                <td>
                  <span class="status-pill" :class="statusClass(sponsor.status)">{{ formatStatus(sponsor.status) }}</span>
                </td>
                <td>{{ sponsor.onboardDate || '-' }}</td>
                <td>
                  <button class="btn small" @click="editSponsor(sponsor)">Edit</button>
                  <button class="btn small danger" @click="removeSponsor(sponsor.id)">Delete</button>
                  <button class="btn small ghost" @click="openCampaignPanel(sponsor)">Select for Campaign</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state">No sponsors yet. Create your first sponsor profile to begin.</div>
        </div>
      </div>

      <div class="section campaign-section accordion-card" :class="{ open: openPanels.campaignCreate }">
        <button class="section-toggle" type="button" @click="togglePanel('campaignCreate')">
          <div class="section-header">
            <div>
              <h2>3. Campaign Set / Create</h2>
              <p class="section-subtitle">Create or edit campaign details for the selected sponsor.</p>
            </div>
            <div class="section-meta">
              <span class="toggle-icon" :class="{ rotated: openPanels.campaignCreate }">⌄</span>
            </div>
          </div>
        </button>

        <div v-show="openPanels.campaignCreate" class="section-body">
        <p class="selected-sponsor" v-if="selectedSponsor">Selected Sponsor: <strong>{{ selectedSponsor.sponsorName }}</strong></p>
        <div v-else class="empty-state">Select a sponsor from Sponsor List first to create campaigns.</div>

        <template v-if="selectedSponsor">
        <form class="grid campaign-form form-shell" @submit.prevent="saveCampaign">
          <div class="group-title field-wide">Campaign Details</div>
          <label class="field">
            <span>Brand/Product Name</span>
            <input v-model.trim="campaignForm.campaignName" placeholder="Brand or product name" required />
          </label>
          <label class="field field-wide">
            <span>Event Name (Multiple)</span>
            <div class="category-picker">
              <div class="selected-tags" v-if="campaignForm.eventTags.length">
                <span class="category-tag" v-for="tag in campaignForm.eventTags" :key="`event-${tag}`">
                  {{ tag }}
                  <button type="button" class="tag-remove" @click="removeEventTag(tag)">×</button>
                </span>
              </div>
              <div class="selected-tags empty" v-else>
                No event selected
              </div>
              <div class="event-search-row">
                <input
                  v-model.trim="eventSearchInput"
                  type="text"
                  placeholder="Search previous events or type new event and press Enter"
                  @input="onEventSearchInput"
                  @keydown.enter.prevent="addEventTagFromInput"
                />
                <button type="button" class="btn small" @click="addEventTagFromInput">Add</button>
              </div>
              <div class="event-suggestions" v-if="eventSuggestions.length">
                <button
                  type="button"
                  class="category-option"
                  v-for="suggestion in eventSuggestions"
                  :key="`event-suggest-${suggestion}`"
                  @click="selectSuggestedEvent(suggestion)"
                >
                  {{ suggestion }}
                </button>
              </div>
              <small class="field-note" v-if="eventSuggestionLoading">Loading suggestions...</small>
            </div>
          </label>
          <label class="field">
            <span>Campaign Start Date</span>
            <input v-model="campaignForm.startDate" type="date" />
          </label>
          <label class="field">
            <span>Campaign End Date</span>
            <input v-model="campaignForm.endDate" type="date" />
          </label>
          <label class="field field-wide">
            <span>Content Category (Multiple)</span>
            <div class="category-picker">
              <div class="selected-tags" v-if="campaignForm.contentCategory.length">
                <span class="category-tag" v-for="category in campaignForm.contentCategory" :key="`selected-${category}`">
                  {{ category }}
                  <button type="button" class="tag-remove" @click="removeContentCategory(category)">×</button>
                </span>
              </div>
              <div class="selected-tags empty" v-else>
                No category selected
              </div>

              <div class="category-options">
                <button
                  type="button"
                  v-for="category in contentCategoryOptions"
                  :key="category"
                  class="category-option"
                  :class="{ active: isContentCategorySelected(category) }"
                  @click="toggleContentCategory(category)"
                >
                  {{ category }}
                </button>
              </div>
            </div>
            <small class="field-note">Click categories to add/remove tags.</small>
          </label>
          <div class="field field-wide">
            <span>Category-wise Content Distribution</span>
            <div class="distribution-editor">
              <div class="distribution-total-row">
                <label class="distribution-total-label">Total Quantity</label>
                <input v-model.number="campaignForm.numberOfContent" type="number" min="0" placeholder="e.g. 100" class="distribution-total-input" @input="onTotalQuantityChange" />
                <span class="distribution-total-hint">Set total first to enable auto-calculation</span>
              </div>
              <div class="distribution-row distribution-head">
                <strong>Category</strong>
                <strong>Percent (%)</strong>
                <strong>Quantity</strong>
              </div>
              <div class="distribution-row" v-for="(item, index) in getDistributionRows()" :key="`dist-${item.category}-${index}`">
                <span>{{ item.category }}</span>
                <input v-model.number="item.percent" type="number" min="0" max="100" step="0.01" @input="onPercentInput(index)" />
                <input v-model.number="item.quantity" type="number" min="0" step="1" @input="onQuantityInput(index)" />
              </div>
              <div class="distribution-footer">
                <span>Total: <strong>{{ getDistributionTotal() }}%</strong></span>
                <div class="distribution-actions">
                  <button class="btn small ghost" type="button" @click="autoFillOthersDistribution">Auto Fill Others</button>
                  <button
                    class="btn small ghost"
                    type="button"
                    v-if="!getDistributionRows().some(item => String(item.category || '').toLowerCase() === 'others')"
                    @click="addOthersDistribution"
                  >Add Others</button>
                  <button
                    class="btn small danger"
                    type="button"
                    v-else
                    @click="removeOthersDistribution"
                  >Remove Others</button>
                </div>
              </div>
            </div>
            <small class="field-note">Total should be 100%. Others can be auto-balanced.</small>
          </div>
          <label class="field">
            <span>Number of Content</span>
            <input v-model.trim="campaignForm.numberOfContent" type="number" min="0" placeholder="Total content" />
          </label>
          <label class="field">
            <span>Number of Daily Content</span>
            <input v-model.trim="campaignForm.dailyContentCount" type="number" min="0" placeholder="Optional if not daily" />
          </label>
          <div class="field field-wide">
            <span>Exact Schedule (Optional Day-wise Planner)</span>
            <div class="schedule-editor">
              <div class="schedule-head">
                <strong>Date</strong>
                <strong>Category</strong>
                <strong>Count</strong>
                <strong>Instruction</strong>
                <strong>Note</strong>
                <strong>Action</strong>
              </div>
              <div class="schedule-row" v-for="(entry, index) in campaignForm.scheduleEntries" :key="`schedule-${index}`">
                <input v-model="entry.date" type="date" />
                <input v-model.trim="entry.category" placeholder="Category" />
                <input v-model.number="entry.plannedCount" type="number" min="0" placeholder="0" />
                <input v-model.trim="entry.instruction" placeholder="Instruction" />
                <input v-model.trim="entry.note" placeholder="Note" />
                <button class="btn small danger" type="button" @click="removeScheduleEntry(index)">Remove</button>
              </div>
              <div class="schedule-actions">
                <button class="btn small ghost" type="button" @click="addScheduleEntry">Add Day-wise Row</button>
              </div>
            </div>
          </div>
          <label class="field field-wide">
            <span>Remarks</span>
            <textarea v-model.trim="campaignForm.remarks" placeholder="Campaign remarks"></textarea>
          </label>
          <label class="field field-wide">
            <span>Creative</span>
            <textarea v-model.trim="campaignForm.creative" placeholder="Creative details"></textarea>
          </label>
          <div class="group-title field-wide">Featured Content</div>
          <label class="field">
            <span>Video Platform Distribution</span>
            <select v-model="campaignForm.videoPlatformDistribution">
              <option value="fb">FB</option>
              <option value="yt">YT</option>
              <option value="both">Both</option>
            </select>
          </label>

          <div class="group-title field-wide">Brandings</div>
          <label class="field-check">
            <input v-model="campaignForm.brandingThumbnail" type="checkbox" />
            <span>Thumbnail</span>
          </label>
          <label class="field-check">
            <input v-model="campaignForm.brandingDog" type="checkbox" />
            <span>Dog</span>
          </label>
          <label class="field-check">
            <input v-model="campaignForm.brandingStatic" type="checkbox" />
            <span>Static</span>
          </label>
          <label class="field-check">
            <input v-model="campaignForm.brandingPhotocard" type="checkbox" />
            <span>Photocard</span>
          </label>
          <label class="field-check">
            <input v-model="campaignForm.brandingReels" type="checkbox" />
            <span>Reels</span>
          </label>
          <label class="field">
            <span>Lshape Variations</span>
            <input v-model.trim="campaignForm.brandingLshapeVariations" placeholder="2/3 variations" />
          </label>
          <label class="field">
            <span>OVC Variations</span>
            <input v-model.trim="campaignForm.brandingOvcVariations" placeholder="2/3 variations" />
          </label>
          <label class="field field-wide">
            <span>Instruction</span>
            <textarea v-model.trim="campaignForm.instruction" placeholder="Detailed instruction"></textarea>
          </label>
          <div class="action-row field-wide">
            <button class="btn" type="submit">{{ campaignForm.id ? 'Update Campaign' : 'Create Campaign' }}</button>
            <button v-if="campaignForm.id" class="btn secondary" type="button" @click="resetCampaignForm">Cancel Edit</button>
          </div>
        </form>

        <div class="file-requirements-section">
          <div class="file-req-header">
            <h3>File Requirements</h3>
            <p>Drag & drop or select files for each required creative label. Each label supports multiple files with limits.</p>
          </div>

          <div v-if="!campaignForm.id" class="empty-state">
            Save this campaign first, then upload requirement files to S3.
          </div>

          <div v-else class="requirement-grid">
            <div class="requirement-card" v-for="requirement in fileRequirementsConfig" :key="requirement.key">
              <div class="requirement-top">
                <h4>{{ requirement.label }}</h4>
                <span class="limit-badge">{{ getRequirementFiles(requirement.key).length }} / {{ requirement.maxFiles }}</span>
              </div>
              <p class="requirement-meta">Allowed: {{ requirement.formats }} • Max {{ requirement.maxFiles }}</p>

              <div
                class="upload-dropzone"
                :class="{ dragover: dragOverByLabel[requirement.key], disabled: uploadingByLabel[requirement.key] || remainingRequirementSlots(requirement) === 0 }"
                @dragenter.prevent="setDragOver(requirement.key, true)"
                @dragover.prevent="setDragOver(requirement.key, true)"
                @dragleave.prevent="setDragOver(requirement.key, false)"
                @drop.prevent="onRequirementDrop($event, requirement)"
              >
                <p>Drop files here</p>
                <button
                  class="btn small ghost"
                  type="button"
                  :disabled="uploadingByLabel[requirement.key] || remainingRequirementSlots(requirement) === 0"
                  @click="triggerRequirementPicker(requirement.key)"
                >
                  {{ uploadingByLabel[requirement.key] ? 'Uploading...' : 'Select Files' }}
                </button>
                <input
                  :ref="`fileInput-${requirement.key}`"
                  type="file"
                  :accept="requirement.accept"
                  multiple
                  class="hidden-input"
                  @change="onRequirementFileSelect($event, requirement)"
                />
              </div>

              <ul class="uploaded-list" v-if="getRequirementFiles(requirement.key).length">
                <li v-for="file in getRequirementFiles(requirement.key)" :key="file.key || file.url">
                  <div class="uploaded-item">
                    <div class="file-preview">
                      <img
                        v-if="isImageFile(file)"
                        :src="file.url"
                        :alt="file.name || 'Preview image'"
                        class="preview-media"
                      />
                      <video
                        v-else-if="isVideoFile(file)"
                        :src="file.url"
                        class="preview-media"
                        muted
                        playsinline
                        preload="metadata"
                      ></video>
                      <div v-else class="preview-fallback">{{ fileExtensionLabel(file) }}</div>
                    </div>
                    <div class="file-meta">
                      <a :href="file.url" target="_blank" rel="noopener noreferrer">{{ file.name || 'Uploaded file' }}</a>
                      <span class="file-sub">Quick preview available</span>
                    </div>
                  </div>
                </li>
              </ul>
              <p v-else class="no-files">No files uploaded yet</p>

              <!-- Per-file upload progress bars -->
              <div
                v-if="uploadProgressByLabel[requirement.key] && uploadProgressByLabel[requirement.key].length"
                class="upload-progress-list"
              >
                <div
                  v-for="(entry, idx) in uploadProgressByLabel[requirement.key]"
                  :key="idx"
                  class="upload-progress-entry"
                  :class="entry.status"
                >
                  <div class="upload-progress-header">
                    <span class="upload-progress-name">{{ entry.name }}</span>
                    <span class="upload-progress-pct">{{ entry.status === 'done' ? '✓' : entry.status === 'error' ? '✗' : entry.progress + '%' }}</span>
                  </div>
                  <div class="upload-progress-bar-track">
                    <div
                      class="upload-progress-bar-fill"
                      :style="{ width: entry.progress + '%' }"
                      :class="{ done: entry.status === 'done', error: entry.status === 'error' }"
                    ></div>
                  </div>
                  <!-- Inline preview after 100% -->
                  <div v-if="entry.status === 'done' && entry.previewUrl" class="upload-done-preview">
                    <img
                      v-if="entry.previewKind === 'image'"
                      :src="entry.previewUrl"
                      :alt="entry.name"
                      class="done-preview-media"
                    />
                    <video
                      v-else-if="entry.previewKind === 'video'"
                      :src="entry.previewUrl"
                      class="done-preview-media"
                      muted
                      playsinline
                      preload="metadata"
                      controls
                    ></video>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </template>
        </div>
      </div>

      <div class="section campaign-list-section accordion-card" :class="{ open: openPanels.campaignList }">
        <button class="section-toggle" type="button" @click="togglePanel('campaignList')">
          <div class="section-header">
            <div>
              <h2>4. Campaign List</h2>
              <p class="section-subtitle">View all campaigns for the selected sponsor.</p>
            </div>
            <div class="section-meta">
              <span class="count-pill">{{ campaigns.length }} Campaigns</span>
              <span class="toggle-icon" :class="{ rotated: openPanels.campaignList }">⌄</span>
            </div>
          </div>
        </button>

        <div v-show="openPanels.campaignList" class="section-body">
        <p class="selected-sponsor" v-if="selectedSponsor">Showing campaigns for: <strong>{{ selectedSponsor.sponsorName }}</strong></p>
        <div v-else class="empty-state">Select a sponsor from Sponsor List to load campaign list.</div>

        <div class="table-wrap" v-if="selectedSponsor && campaigns.length">
          <table>
            <thead>
              <tr>
                <th>Brand/Product</th>
                <th>Category</th>
                <th>Platform</th>
                <th>Start</th>
                <th>End</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="campaign in campaigns" :key="campaign.id">
                <td class="strong">{{ campaign.campaignName }}</td>
                <td>{{ formatContentCategories(campaign.contentCategory) }}</td>
                <td>{{ formatPlatform(campaign.videoPlatformDistribution) }}</td>
                <td>{{ campaign.startDate || '-' }}</td>
                <td>{{ campaign.endDate || '-' }}</td>
                <td>
                  <button class="btn small" @click="editCampaign(campaign); openPanels.campaignCreate = true">Edit</button>
                  <button class="btn small danger" @click="removeCampaign(campaign.id)">Delete</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else-if="selectedSponsor" class="empty-state">No campaigns found for this sponsor. Add one from Campaign Set / Create.</div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import MenuBar from './MenuBar.vue';
import sponsorService from '../services/sponsorService';
import { uploadFileInChunks } from '../services/chunkedUploadService';

function defaultSponsorForm() {
  return {
    id: null,
    sponsorName: '',
    sponsorLogo: '',
    sponsorAddress: '',
    officeNumber: '',
    contactPerson1: '',
    contactPerson2: '',
    contactNumber1: '',
    contactNumber2: '',
    onboardDate: '',
    previousNote: '',
    sponsorLevel: '',
    status: 'good',
  };
}

function defaultCampaignForm() {
  return {
    id: null,
    campaignName: '',
    startDate: '',
    endDate: '',
    contentCategory: [],
    distributionRatio: '',
    numberOfContent: '',
    exactSchedule: '',
    dailyContentCount: '',
    remarks: '',
    creative: '',
    videoPlatformDistribution: 'fb',
    brandingThumbnail: false,
    brandingDog: false,
    brandingStatic: true,
    brandingPhotocard: false,
    brandingReels: false,
    brandingLshapeVariations: '',
    brandingOvcVariations: '',
    instruction: '',
    photoType: 'static',
    eventTags: [],
    eventSearchTokens: [],
    contentDistribution: {
      items: [],
      totalPercent: 0,
      hasAutoOthers: false,
    },
    scheduleEntries: [],
    fileRequirements: {},
  };
}

export default {
  name: 'SponsorManagement',
  components: {
    MenuBar,
  },
  watch: {
    sponsorFilters: {
      deep: true,
      handler() {
        this.queueSponsorFilterFetch();
      },
    },
  },
  data() {
    return {
      sponsors: [],
      campaigns: [],
      selectedSponsor: null,
      openPanels: {
        sponsorCreate: false,
        sponsorList: true,
        campaignCreate: false,
        campaignList: true,
      },
      contentCategoryOptions: [
        "politics",
        "national",
        "international",
        "economy",
        "business",
        "technology",
        "science",
        "sports",
        "entertainment",
        "lifestyle",
        "health",
        "education",
        "crime",
        "investigation",
        "environment",
        "weather",
        "opinion",
        "talk_show",
        "breaking_news",
        "live_event",
        "documentary",
        "fact_check"
      ],
      sponsorFilters: {
        name: '',
        status: '',
        onboardFrom: '',
        onboardTo: '',
      },
      sponsorFilterTimer: null,
      eventSearchInput: '',
      eventSuggestions: [],
      eventSuggestionLoading: false,
      fileRequirementsConfig: [
        {
          key: 'doggie',
          label: 'Doggie',
          formats: 'mp4 / mov / png / jpg',
          accept: '.mp4,.mov,.png,.jpg,.jpeg',
          maxFiles: 2,
        },
        {
          key: 'lshape',
          label: 'L-Shape',
          formats: 'mp4 / mov / png / jpg',
          accept: '.mp4,.mov,.png,.jpg,.jpeg',
          maxFiles: 5,
        },
        {
          key: 'reels_shorts',
          label: 'Reels & Shorts',
          formats: 'mp4 / mov / png / jpg',
          accept: '.mp4,.mov,.png,.jpg,.jpeg',
          maxFiles: 5,
        },
        {
          key: 'popup',
          label: 'Popup',
          formats: 'mp4 / mov / png / jpg',
          accept: '.mp4,.mov,.png,.jpg,.jpeg',
          maxFiles: 5,
        },
        {
          key: 'thumbnail_banner',
          label: 'Thumbnail / Ad-banner',
          formats: 'png / jpg / psd',
          accept: '.png,.jpg,.jpeg,.psd',
          maxFiles: 2,
        },
        {
          key: 'ovc',
          label: 'OVC',
          formats: 'mp4 / mov / png / jpg',
          accept: '.mp4,.mov,.png,.jpg,.jpeg',
          maxFiles: 5,
        },
      ],
      uploadingByLabel: {},
      uploadProgressByLabel: {}, // { [labelKey]: [{ name, progress, status, url, previewUrl, previewKind }] }
      dragOverByLabel: {},
      sponsorForm: defaultSponsorForm(),
      campaignForm: defaultCampaignForm(),
      message: '',
    };
  },
  mounted() {
    this.fetchSponsors();
  },
  methods: {
    async fetchSponsors() {
      try {
        const response = await sponsorService.getSponsors(this.sponsorFilters);
        this.sponsors = response.items || [];
      } catch (error) {
        this.sponsors = [];
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load sponsors';
      }
    },
    queueSponsorFilterFetch() {
      if (this.sponsorFilterTimer) {
        clearTimeout(this.sponsorFilterTimer);
      }
      this.sponsorFilterTimer = setTimeout(() => {
        this.fetchSponsors();
      }, 250);
    },
    clearSponsorFilters() {
      this.sponsorFilters = {
        name: '',
        status: '',
        onboardFrom: '',
        onboardTo: '',
      };
    },
    async saveSponsor() {
      try {
        if (this.sponsorForm.id) {
          await sponsorService.updateSponsor(this.sponsorForm.id, this.sponsorForm);
          this.message = 'Sponsor updated';
        } else {
          await sponsorService.createSponsor(this.sponsorForm);
          this.message = 'Sponsor created';
        }
        this.resetSponsorForm();
        await this.fetchSponsors();
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to save sponsor';
      }
    },
    editSponsor(sponsor) {
      this.sponsorForm = { ...sponsor };
      this.openPanels.sponsorCreate = true;
    },
    async removeSponsor(sponsorId) {
      if (!confirm('Delete this sponsor?')) return;
      try {
        await sponsorService.deleteSponsor(sponsorId);
        this.message = 'Sponsor deleted';
        if (this.selectedSponsor && this.selectedSponsor.id === sponsorId) {
          this.selectedSponsor = null;
          this.campaigns = [];
        }
        await this.fetchSponsors();
      } catch (error) {
        this.message = 'Failed to delete sponsor';
      }
    },
    resetSponsorForm() {
      this.sponsorForm = defaultSponsorForm();
    },
    async openCampaignPanel(sponsor) {
      this.selectedSponsor = sponsor;
      this.openPanels.campaignCreate = true;
      this.openPanels.campaignList = true;
      this.resetCampaignForm();
      this.resetEventSuggestionState();
      try {
        const response = await sponsorService.getCampaigns(sponsor.id);
        this.campaigns = response.items || [];
      } catch (error) {
        this.message = 'Failed to load campaigns';
      }
    },
    async saveCampaign() {
      if (!this.selectedSponsor) return;
      const distributionTotal = this.getDistributionTotal();
      if (distributionTotal > 100) {
        this.message = `Distribution total cannot exceed 100. Current total: ${distributionTotal}%`;
        return;
      }
      try {
        let response;
        this.autoFillOthersDistribution();
        const payload = this.prepareCampaignPayload(this.campaignForm);
        if (this.campaignForm.id) {
          response = await sponsorService.updateCampaign(this.campaignForm.id, payload);
          this.message = 'Campaign updated';
        } else {
          response = await sponsorService.createCampaign(this.selectedSponsor.id, payload);
          this.message = 'Campaign created';
        }

        if (response && response.item) {
          this.campaignForm = this.mapCampaignToForm(response.item);
        }

        const campaignsResponse = await sponsorService.getCampaigns(this.selectedSponsor.id);
        this.campaigns = campaignsResponse.items || [];
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to save campaign';
      }
    },
    editCampaign(campaign) {
      this.campaignForm = this.mapCampaignToForm(campaign);
      this.openPanels.campaignCreate = true;
    },
    async removeCampaign(campaignId) {
      if (!confirm('Delete this campaign?')) return;
      try {
        await sponsorService.deleteCampaign(campaignId);
        this.message = 'Campaign deleted';
        await this.openCampaignPanel(this.selectedSponsor);
      } catch (error) {
        this.message = 'Failed to delete campaign';
      }
    },
    resetCampaignForm() {
      this.campaignForm = defaultCampaignForm();
      this.syncDistributionRowsWithCategories();
      this.resetEventSuggestionState();
    },
    togglePanel(panel) {
      this.openPanels[panel] = !this.openPanels[panel];
    },
    formatStatus(status) {
      if (!status) return '-';
      return String(status)
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    formatPlatform(platform) {
      if (!platform) return '-';
      const value = String(platform).toLowerCase();
      if (value === 'fb') return 'FB';
      if (value === 'yt') return 'YT';
      if (value === 'both') return 'Both';
      return platform;
    },
    normalizeContentCategories(value) {
      if (Array.isArray(value)) return value;
      if (!value) return [];
      return String(value)
        .split(/,|\n/)
        .map(item => item.trim())
        .filter(Boolean);
    },
    normalizeEventTags(value) {
      if (Array.isArray(value)) {
        return [...new Set(value.map(item => String(item || '').trim()).filter(Boolean))];
      }
      if (!value) return [];
      return [...new Set(String(value)
        .split(/,|\n/)
        .map(item => item.trim())
        .filter(Boolean))];
    },
    resetEventSuggestionState() {
      this.eventSearchInput = '';
      this.eventSuggestions = [];
      this.eventSuggestionLoading = false;
    },
    async onEventSearchInput() {
      const query = String(this.eventSearchInput || '').trim();
      if (!query) {
        this.eventSuggestions = [];
        return;
      }

      this.eventSuggestionLoading = true;
      try {
        const response = await sponsorService.getCampaignEvents(query, 12);
        const existing = new Set(this.campaignForm.eventTags.map(item => String(item).toLowerCase()));
        this.eventSuggestions = (response.items || [])
          .map(item => String(item || '').trim())
          .filter(Boolean)
          .filter(item => !existing.has(item.toLowerCase()));
      } catch (error) {
        this.eventSuggestions = [];
      } finally {
        this.eventSuggestionLoading = false;
      }
    },
    addEventTag(value) {
      const tag = String(value || '').trim();
      if (!tag) return;
      const exists = this.campaignForm.eventTags.some(item => String(item).toLowerCase() === tag.toLowerCase());
      if (exists) {
        this.eventSearchInput = '';
        this.eventSuggestions = [];
        return;
      }
      this.campaignForm.eventTags = [...this.campaignForm.eventTags, tag];
      this.eventSearchInput = '';
      this.eventSuggestions = [];
    },
    addEventTagFromInput() {
      this.addEventTag(this.eventSearchInput);
    },
    selectSuggestedEvent(tag) {
      this.addEventTag(tag);
    },
    removeEventTag(tag) {
      this.campaignForm.eventTags = this.campaignForm.eventTags.filter(item => item !== tag);
    },
    parsePositiveNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    normalizeContentDistribution(value, categories, legacyRatio) {
      const normalizedCategories = this.normalizeContentCategories(categories);
      let items = [];

      if (value && typeof value === 'object' && !Array.isArray(value) && Array.isArray(value.items)) {
        items = value.items
          .map(item => {
            const category = String(item && item.category || '').trim();
            const percent = this.parsePositiveNumber(item && item.percent);
            const quantity = this.parsePositiveNumber(item && item.quantity);
            if (!category) return null;
            return {
              category,
              percent,
              quantity,
            };
          })
          .filter(Boolean);
      }

      if (!items.length && legacyRatio) {
        const numbers = (String(legacyRatio).match(/\d+(?:\.\d+)?/g) || [])
          .map(item => Number(item))
          .filter(item => Number.isFinite(item) && item > 0);

        items = numbers.map((number, index) => ({
          category: normalizedCategories[index] || `Category ${index + 1}`,
          percent: Number(number),
          quantity: 0,
        }));
      }

      const grouped = {};
      items.forEach(item => {
        const key = String(item.category || '').trim();
        if (!key) return;
        if (!grouped[key]) {
          grouped[key] = {
            category: key,
            percent: 0,
            quantity: 0,
          };
        }
        grouped[key].percent += this.parsePositiveNumber(item.percent);
        grouped[key].quantity += this.parsePositiveNumber(item.quantity);
      });

      const mergedItems = Object.values(grouped);
      let totalPercent = mergedItems.reduce((sum, item) => sum + this.parsePositiveNumber(item.percent), 0);
      let hasAutoOthers = false;

      if (mergedItems.length && totalPercent < 100) {
        const remaining = Number((100 - totalPercent).toFixed(2));
        const others = mergedItems.find(item => String(item.category).toLowerCase() === 'others');
        if (others) {
          others.percent = Number((this.parsePositiveNumber(others.percent) + remaining).toFixed(2));
        } else {
          mergedItems.push({
            category: 'Others',
            percent: remaining,
            quantity: 0,
          });
        }
        totalPercent = 100;
        hasAutoOthers = true;
      }

      return {
        items: mergedItems,
        totalPercent: Number(totalPercent.toFixed(2)),
        hasAutoOthers,
      };
    },
    serializeLegacyDistributionRatio(contentDistribution) {
      const items = contentDistribution && Array.isArray(contentDistribution.items)
        ? contentDistribution.items
        : [];
      return items
        .filter(item => item && item.category)
        .map(item => `${item.category}: ${this.parsePositiveNumber(item.percent)}%`)
        .join(' / ');
    },
    normalizeScheduleEntries(value, legacyExactSchedule) {
      if (Array.isArray(value) && value.length) {
        return value
          .filter(item => item && typeof item === 'object')
          .map(item => ({
            date: String(item.date || '').trim(),
            category: String(item.category || '').trim(),
            plannedCount: this.parsePositiveNumber(item.plannedCount),
            instruction: String(item.instruction || '').trim(),
            note: String(item.note || '').trim(),
          }))
          .filter(item => item.date || item.category || item.plannedCount || item.instruction || item.note);
      }

      if (!legacyExactSchedule) return [];

      return [{
        date: '',
        category: '',
        plannedCount: 0,
        instruction: String(legacyExactSchedule || '').trim(),
        note: '',
      }];
    },
    serializeLegacyExactSchedule(scheduleEntries) {
      if (!Array.isArray(scheduleEntries) || !scheduleEntries.length) return '';
      return scheduleEntries
        .map(item => {
          const parts = [];
          if (item.date) parts.push(item.date);
          if (item.category) parts.push(item.category);
          if (item.plannedCount) parts.push(`count=${item.plannedCount}`);
          if (item.instruction) parts.push(`instruction=${item.instruction}`);
          if (item.note) parts.push(`note=${item.note}`);
          return parts.join(' | ');
        })
        .filter(Boolean)
        .join('\n');
    },
    prepareCampaignPayload(form) {
      const contentDistribution = this.normalizeContentDistribution(form.contentDistribution, form.contentCategory, form.distributionRatio);
      const scheduleEntries = this.normalizeScheduleEntries(form.scheduleEntries, form.exactSchedule);
      const eventTags = this.normalizeEventTags(form.eventTags);
      const eventSearchTokens = [...new Set(eventTags
        .flatMap(tag => [String(tag).toLowerCase(), ...String(tag).toLowerCase().split(/\s+/)])
        .filter(Boolean))];

      return {
        ...form,
        eventTags,
        eventSearchTokens,
        contentDistribution,
        scheduleEntries,
        distributionRatio: form.distributionRatio || this.serializeLegacyDistributionRatio(contentDistribution),
        exactSchedule: form.exactSchedule || this.serializeLegacyExactSchedule(scheduleEntries),
      };
    },
    formatContentCategories(value) {
      const categories = this.normalizeContentCategories(value);
      return categories.length ? categories.join(', ') : '-';
    },
    syncDistributionRowsWithCategories() {
      const selected = this.campaignForm.contentCategory;
      const existingRows = (this.campaignForm.contentDistribution && this.campaignForm.contentDistribution.items) || [];
      const existingMap = {};
      existingRows.forEach(row => {
        const key = String(row.category || '').toLowerCase();
        if (!key) return;
        existingMap[key] = {
          category: row.category,
          percent: this.parsePositiveNumber(row.percent),
          quantity: this.parsePositiveNumber(row.quantity),
        };
      });

      const rebuiltRows = selected.map(category => {
        const key = String(category).toLowerCase();
        return existingMap[key] || {
          category,
          percent: 0,
          quantity: 0,
        };
      });

      if (existingMap.others) {
        rebuiltRows.push(existingMap.others);
      }

      this.campaignForm.contentDistribution = {
        items: rebuiltRows,
        totalPercent: this.getDistributionTotal(rebuiltRows),
        hasAutoOthers: Boolean(existingMap.others),
      };
    },
    getDistributionRows() {
      if (!this.campaignForm.contentDistribution || !Array.isArray(this.campaignForm.contentDistribution.items)) {
        return [];
      }
      return this.campaignForm.contentDistribution.items;
    },
    getDistributionTotal(rows) {
      const sourceRows = Array.isArray(rows) ? rows : this.getDistributionRows();
      return Number(sourceRows.reduce((sum, item) => sum + this.parsePositiveNumber(item.percent), 0).toFixed(2));
    },
    updateDistributionTotals() {
      const total = this.getDistributionTotal();
      this.campaignForm.contentDistribution = {
        ...(this.campaignForm.contentDistribution || {}),
        items: this.getDistributionRows(),
        totalPercent: total,
        hasAutoOthers: this.getDistributionRows().some(item => String(item.category || '').toLowerCase() === 'others'),
      };
    },
    addOthersDistribution() {
      const hasOthers = this.getDistributionRows().some(item => String(item.category || '').toLowerCase() === 'others');
      if (hasOthers) return;
      this.campaignForm.contentDistribution.items = [
        ...this.getDistributionRows(),
        {
          category: 'Others',
          percent: 0,
          quantity: 0,
        },
      ];
      this.updateDistributionTotals();
    },
    removeOthersDistribution() {
      this.campaignForm.contentDistribution.items = this.getDistributionRows().filter(item => String(item.category || '').toLowerCase() !== 'others');
      this.updateDistributionTotals();
    },
    autoFillOthersDistribution() {
      const rows = this.getDistributionRows();
      const othersIndex = rows.findIndex(item => String(item.category || '').toLowerCase() === 'others');
      const nonOthersTotal = rows
        .filter((item, index) => index !== othersIndex)
        .reduce((sum, item) => sum + this.parsePositiveNumber(item.percent), 0);

      const remaining = Number(Math.max(100 - nonOthersTotal, 0).toFixed(2));
      const autoTotal = Number(this.campaignForm.numberOfContent);
      if (othersIndex >= 0) {
        this.campaignForm.contentDistribution.items[othersIndex].percent = remaining;
        if (autoTotal > 0) {
          this.campaignForm.contentDistribution.items[othersIndex].quantity = Math.round(autoTotal * remaining / 100);
        }
      } else if (remaining > 0 && rows.length) {
        this.addOthersDistribution();
        const currentRows = this.getDistributionRows();
        const index = currentRows.findIndex(item => String(item.category || '').toLowerCase() === 'others');
        if (index >= 0) {
          this.campaignForm.contentDistribution.items[index].percent = remaining;
          if (autoTotal > 0) {
            this.campaignForm.contentDistribution.items[index].quantity = Math.round(autoTotal * remaining / 100);
          }
        }
      }
      this.updateDistributionTotals();
    },
    onDistributionInputChange() {
      this.updateDistributionTotals();
    },
    onPercentInput(index) {
      const rows = this.getDistributionRows();
      const item = rows[index];
      const total = Number(this.campaignForm.numberOfContent);
      if (item && total > 0) {
        item.quantity = Math.round(total * this.parsePositiveNumber(item.percent) / 100);
      }
      this.updateDistributionTotals();
    },
    onQuantityInput(index) {
      const rows = this.getDistributionRows();
      const item = rows[index];
      const total = Number(this.campaignForm.numberOfContent);
      if (item && total > 0) {
        item.percent = Number((this.parsePositiveNumber(item.quantity) / total * 100).toFixed(2));
      }
      this.updateDistributionTotals();
    },
    onTotalQuantityChange() {
      const total = Number(this.campaignForm.numberOfContent);
      if (total <= 0) {
        this.updateDistributionTotals();
        return;
      }
      const rows = this.getDistributionRows();
      rows.forEach(item => {
        if (item.percent > 0) {
          item.quantity = Math.round(total * this.parsePositiveNumber(item.percent) / 100);
        }
      });
      this.updateDistributionTotals();
    },
    addScheduleEntry() {
      this.campaignForm.scheduleEntries = [
        ...this.campaignForm.scheduleEntries,
        {
          date: '',
          category: '',
          plannedCount: 0,
          instruction: '',
          note: '',
        },
      ];
    },
    removeScheduleEntry(index) {
      this.campaignForm.scheduleEntries = this.campaignForm.scheduleEntries.filter((_, rowIndex) => rowIndex !== index);
    },
    isContentCategorySelected(category) {
      return this.campaignForm.contentCategory.includes(category);
    },
    toggleContentCategory(category) {
      const isSelected = this.isContentCategorySelected(category);
      if (isSelected) {
        this.campaignForm.contentCategory = this.campaignForm.contentCategory.filter(item => item !== category);
        this.syncDistributionRowsWithCategories();
        return;
      }
      this.campaignForm.contentCategory = [...this.campaignForm.contentCategory, category];
      this.syncDistributionRowsWithCategories();
    },
    removeContentCategory(category) {
      this.campaignForm.contentCategory = this.campaignForm.contentCategory.filter(item => item !== category);
      this.syncDistributionRowsWithCategories();
    },
    fileExtensionLabel(file) {
      const extension = (file && file.extension) || ((file && file.name && file.name.split('.').pop()) || 'FILE');
      return String(extension).toUpperCase();
    },
    isImageFile(file) {
      const extension = String((file && file.extension) || '').toLowerCase();
      return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(extension);
    },
    isVideoFile(file) {
      const extension = String((file && file.extension) || '').toLowerCase();
      return ['mp4', 'mov', 'webm'].includes(extension);
    },
    mapCampaignToForm(campaign) {
      const eventTags = this.normalizeEventTags(campaign.eventTags);
      const contentDistribution = this.normalizeContentDistribution(
        campaign.contentDistribution,
        campaign.contentCategory,
        campaign.distributionRatio
      );
      const scheduleEntries = this.normalizeScheduleEntries(campaign.scheduleEntries, campaign.exactSchedule);

      return {
        ...defaultCampaignForm(),
        ...campaign,
        contentCategory: this.normalizeContentCategories(campaign.contentCategory),
        eventTags,
        eventSearchTokens: Array.isArray(campaign.eventSearchTokens)
          ? [...new Set(campaign.eventSearchTokens.map(item => String(item || '').toLowerCase()).filter(Boolean))]
          : [...new Set(eventTags
            .flatMap(tag => [String(tag).toLowerCase(), ...String(tag).toLowerCase().split(/\s+/)])
            .filter(Boolean))],
        contentDistribution,
        scheduleEntries,
        distributionRatio: campaign.distributionRatio || this.serializeLegacyDistributionRatio(contentDistribution),
        exactSchedule: campaign.exactSchedule || this.serializeLegacyExactSchedule(scheduleEntries),
        fileRequirements: this.normalizeFileRequirements(campaign.fileRequirements),
      };
    },
    normalizeFileRequirements(value) {
      if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
      const normalized = {};
      Object.keys(value).forEach(key => {
        normalized[key] = Array.isArray(value[key]) ? value[key] : [];
      });
      return normalized;
    },
    getRequirementFiles(labelKey) {
      const fileRequirements = this.normalizeFileRequirements(this.campaignForm.fileRequirements);
      return fileRequirements[labelKey] || [];
    },
    remainingRequirementSlots(requirement) {
      const used = this.getRequirementFiles(requirement.key).length;
      return Math.max(requirement.maxFiles - used, 0);
    },
    triggerRequirementPicker(labelKey) {
      const refName = `fileInput-${labelKey}`;
      const refValue = this.$refs[refName];
      if (!refValue) return;
      const input = Array.isArray(refValue) ? refValue[0] : refValue;
      if (input) input.click();
    },
    setDragOver(labelKey, isOver) {
      this.$set(this.dragOverByLabel, labelKey, isOver);
    },
    async onRequirementFileSelect(event, requirement) {
      const files = Array.from((event && event.target && event.target.files) || []);
      event.target.value = '';
      await this.uploadRequirementFiles(requirement, files);
    },
    async onRequirementDrop(event, requirement) {
      this.setDragOver(requirement.key, false);
      const files = Array.from((event && event.dataTransfer && event.dataTransfer.files) || []);
      await this.uploadRequirementFiles(requirement, files);
    },
    async uploadRequirementFiles(requirement, files) {
      if (!this.campaignForm.id) {
        this.message = 'Save campaign first, then upload required files.';
        return;
      }

      if (!files.length) return;

      const remaining = this.remainingRequirementSlots(requirement);
      if (remaining <= 0) {
        this.message = `${requirement.label} already reached max ${requirement.maxFiles} files.`;
        return;
      }

      const MAX_SIZE = 5 * 1024 * 1024 * 1024;
      const acceptedExtensions = requirement.accept
        .split(',')
        .map(item => item.trim().toLowerCase());

      const validFiles = files.filter(file => {
        if (file.size > MAX_SIZE) {
          this.message = `"${file.name}" exceeds the 5 GB upload limit.`;
          return false;
        }
        const fileName = (file.name || '').toLowerCase();
        return acceptedExtensions.some(ext => fileName.endsWith(ext));
      });

      if (!validFiles.length) {
        if (!this.message) {
          this.message = `No valid files for ${requirement.label}. Allowed: ${requirement.formats}`;
        }
        return;
      }

      const uploadBatch = validFiles.slice(0, remaining);
      this.$set(this.uploadingByLabel, requirement.key, true);

      // Initialise progress entries for this batch
      const progressEntries = uploadBatch.map(file => ({
        name: file.name,
        progress: 0,
        status: 'uploading', // 'uploading' | 'done' | 'error'
        url: null,
        previewUrl: null,
        previewKind: null,
      }));
      const existing = this.uploadProgressByLabel[requirement.key] || [];
      this.$set(this.uploadProgressByLabel, requirement.key, [...existing, ...progressEntries]);

      let lastUpdatedItem = null;

      for (let i = 0; i < uploadBatch.length; i++) {
        const file = uploadBatch[i];
        const entryIndex = existing.length + i;
        try {
          const result = await uploadFileInChunks(file, {
            context: 'campaign_file',
            campaignId: this.campaignForm.id,
            label: requirement.key,
            onProgress: (pct) => {
              const list = [...(this.uploadProgressByLabel[requirement.key] || [])];
              if (list[entryIndex]) {
                list[entryIndex] = { ...list[entryIndex], progress: pct };
                this.$set(this.uploadProgressByLabel, requirement.key, list);
              }
            },
          });

          // Determine preview kind
          const ext = (file.name || '').split('.').pop().toLowerCase();
          const previewKind = ['jpg', 'jpeg', 'png'].includes(ext)
            ? 'image'
            : ['mp4', 'mov'].includes(ext)
            ? 'video'
            : null;

          const list = [...(this.uploadProgressByLabel[requirement.key] || [])];
          if (list[entryIndex]) {
            list[entryIndex] = {
              ...list[entryIndex],
              progress: 100,
              status: 'done',
              url: result.url,
              previewUrl: result.url,
              previewKind,
            };
            this.$set(this.uploadProgressByLabel, requirement.key, list);
          }

          lastUpdatedItem = result.item;
        } catch (error) {
          const list = [...(this.uploadProgressByLabel[requirement.key] || [])];
          if (list[entryIndex]) {
            list[entryIndex] = { ...list[entryIndex], status: 'error' };
            this.$set(this.uploadProgressByLabel, requirement.key, list);
          }
          this.message = error.message || `Failed to upload "${file.name}"`;
        }
      }

      if (lastUpdatedItem) {
        this.campaignForm = this.mapCampaignToForm(lastUpdatedItem);
        this.campaigns = this.campaigns.map(item =>
          item.id === lastUpdatedItem.id ? lastUpdatedItem : item
        );
        this.message = `${uploadBatch.length} file(s) uploaded for ${requirement.label}`;
      }

      this.$set(this.uploadingByLabel, requirement.key, false);
    },
    statusClass(status) {
      return `status-${String(status || '').toLowerCase()}`;
    },
  },
};
</script>

<style scoped>
.sponsor-page {
  display: flex;
  min-height: 100vh;
  background: radial-gradient(circle at 6% 8%, #dff4ec 0%, transparent 22%), radial-gradient(circle at 88% 14%, #e9f0ff 0%, transparent 30%), #f3f6fb;
}

.content {
  flex: 1;
  margin-left: 250px;
  padding: 24px;
}

.page-header {
  margin-bottom: 18px;
  background: #fff;
  border: 1px solid #dce6f2;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 8px 18px rgba(15, 23, 42, 0.04);
}

.page-header h1 {
  margin: 0;
  font-size: 28px;
  color: #1f2d3d;
}

.page-header p {
  margin: 8px 0 0;
  color: #526b83;
}

.filters-shell {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
  padding: 12px;
  border-radius: 10px;
  border: 1px solid #dbe5f2;
  background: linear-gradient(140deg, #f9fcff 0%, #f3f9ff 100%);
}

.filter-actions {
  display: flex;
  align-items: flex-end;
}

.section {
  background: #fff;
  border-radius: 14px;
  padding: 20px;
  margin-bottom: 20px;
  border: 1px solid #e7edf4;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.05);
}

.accordion-card {
  padding: 0;
  overflow: hidden;
}

.section-toggle {
  width: 100%;
  border: none;
  background: linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
  cursor: pointer;
  text-align: left;
  padding: 18px 20px;
}

.section-body {
  padding: 0 20px 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0;
}

.section-header h2 {
  margin: 0;
  color: #233244;
}

.section-header p {
  margin: 0;
  color: #617286;
}

.section-subtitle {
  margin-top: 5px !important;
  color: #688097 !important;
  font-size: 13px;
}

.section-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.count-pill {
  background: #e9f0ff;
  color: #1f4eb4;
  font-size: 12px;
  font-weight: 700;
  padding: 6px 10px;
  border-radius: 999px;
}

.toggle-icon {
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2f4e79;
  background: #edf3ff;
  font-size: 18px;
  transition: transform 0.2s ease;
}

.toggle-icon.rotated {
  transform: rotate(180deg);
}

.selected-sponsor {
  margin: 0 0 12px;
  color: #526579;
}

.grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 16px;
}

.form-shell {
  border: 1px solid #e9eef5;
  border-radius: 12px;
  padding: 14px;
  background: linear-gradient(180deg, #fbfdff 0%, #f8fbff 100%);
}

.group-title {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2c5cc5;
  border-left: 4px solid #2c5cc5;
  padding: 6px 10px;
  background: #edf3ff;
  border-radius: 8px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field span {
  font-size: 12px;
  color: #5f6f80;
  font-weight: 600;
}

.field-note {
  margin-top: 2px;
  color: #6b7f93;
  font-size: 11px;
}

.field-check {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 40px;
  padding: 8px 10px;
  border: 1px solid #d7dee7;
  border-radius: 8px;
  background: #fff;
}

.field-check span {
  font-size: 13px;
  color: #44586d;
  font-weight: 600;
}

.field-wide {
  grid-column: span 3;
}

input,
select,
textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d7dee7;
  border-radius: 8px;
  padding: 10px 12px;
  font-size: 14px;
  color: #1f2d3d;
  background: #fff;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.category-picker {
  border: 1px solid #d7dee7;
  border-radius: 8px;
  background: #fff;
  padding: 10px;
}

.selected-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 10px;
}

.selected-tags.empty {
  color: #7a8ca0;
  font-size: 12px;
  font-weight: 500;
}

.category-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #e9f0ff;
  color: #1f4eb4;
  border-radius: 999px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 700;
}

.tag-remove {
  border: none;
  background: transparent;
  color: #1f4eb4;
  font-size: 14px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
}

.category-options {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.event-search-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 8px;
}

.event-suggestions {
  margin-top: 8px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.distribution-editor {
  border: 1px solid #d7dee7;
  border-radius: 8px;
  padding: 10px;
  background: #fff;
}

.distribution-total-row {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
  padding: 8px 10px;
  background: #f0f6ff;
  border: 1px solid #c8daff;
  border-radius: 8px;
}

.distribution-total-label {
  font-size: 12px;
  font-weight: 700;
  color: #2c5cc5;
  white-space: nowrap;
  min-width: 90px;
}

.distribution-total-input {
  width: 120px !important;
  flex-shrink: 0;
}

.distribution-total-hint {
  font-size: 11px;
  color: #5a739c;
  font-style: italic;
}

.distribution-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr;
  gap: 8px;
  align-items: center;
  margin-bottom: 8px;
}

.distribution-head {
  margin-bottom: 10px;
  color: #4a6178;
  font-size: 12px;
}

.distribution-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  border-top: 1px solid #e6edf7;
  padding-top: 8px;
}

.distribution-actions {
  display: flex;
  gap: 8px;
}

.schedule-editor {
  border: 1px solid #d7dee7;
  border-radius: 8px;
  background: #fff;
  padding: 10px;
}

.schedule-head,
.schedule-row {
  display: grid;
  grid-template-columns: 140px 1fr 90px 1fr 1fr 90px;
  gap: 8px;
  align-items: center;
}

.schedule-head {
  color: #4a6178;
  font-size: 12px;
  margin-bottom: 8px;
}

.schedule-row {
  margin-bottom: 8px;
}

.schedule-actions {
  display: flex;
  justify-content: flex-end;
}

.category-option {
  border: 1px solid #cfdaea;
  background: #fff;
  color: #3f5469;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
}

.category-option.active {
  border-color: #2563eb;
  background: #eef4ff;
  color: #1d4ed8;
}

input:focus,
select:focus,
textarea:focus {
  outline: none;
  border-color: #2563eb;
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

input[type='checkbox'] {
  width: 16px;
  height: 16px;
  padding: 0;
  border-radius: 4px;
  box-shadow: none;
}

.grid textarea {
  min-height: 88px;
  resize: vertical;
}

.action-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.file-requirements-section {
  margin-top: 18px;
  border: 1px solid #dde6f2;
  border-radius: 14px;
  padding: 14px;
  background: linear-gradient(180deg, #f7fbff 0%, #f2f7ff 100%);
}

.file-req-header h3 {
  margin: 0;
  color: #1f3654;
}

.file-req-header p {
  margin: 6px 0 14px;
  color: #5b738c;
  font-size: 13px;
}

.requirement-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}

.requirement-card {
  border: 1px solid #dbe5f2;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
}

.requirement-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.requirement-top h4 {
  margin: 0;
  color: #233244;
}

.limit-badge {
  background: #e9f0ff;
  color: #1f4eb4;
  font-size: 11px;
  font-weight: 700;
  border-radius: 999px;
  padding: 4px 8px;
}

.requirement-meta {
  margin: 8px 0;
  color: #60758d;
  font-size: 12px;
}

.upload-dropzone {
  border: 1px dashed #b9c9de;
  border-radius: 10px;
  padding: 12px;
  text-align: center;
  background: #f9fbff;
}

.upload-dropzone p {
  margin: 0 0 8px;
  color: #4e657d;
  font-size: 12px;
}

.upload-dropzone.dragover {
  border-color: #2563eb;
  background: #eef4ff;
}

.upload-dropzone.disabled {
  opacity: 0.65;
}

.hidden-input {
  display: none;
}

.uploaded-list {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.uploaded-list li a {
  color: #1d4ed8;
  text-decoration: none;
  font-size: 12px;
}

.uploaded-item {
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-preview {
  width: 58px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #d8e3f1;
  background: #f4f8ff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  flex-shrink: 0;
}

.preview-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.preview-fallback {
  font-size: 10px;
  font-weight: 700;
  color: #45648b;
  letter-spacing: 0.04em;
}

.file-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.file-meta a {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 190px;
}

.file-sub {
  font-size: 11px;
  color: #6d8196;
}

.no-files {
  margin: 10px 0 0;
  color: #73879a;
  font-size: 12px;
}

/* ---- Upload progress bars ---- */
.upload-progress-list {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.upload-progress-entry {
  background: #f7f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
}

.upload-progress-entry.done {
  border-color: #86efac;
  background: #f0fdf4;
}

.upload-progress-entry.error {
  border-color: #fca5a5;
  background: #fff5f5;
}

.upload-progress-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 5px;
}

.upload-progress-name {
  font-size: 12px;
  color: #374151;
  font-weight: 500;
  max-width: 75%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.upload-progress-pct {
  font-size: 12px;
  font-weight: 600;
  color: #4f6ef2;
}

.done .upload-progress-pct { color: #16a34a; }
.error .upload-progress-pct { color: #dc2626; }

.upload-progress-bar-track {
  height: 6px;
  border-radius: 4px;
  background: #e5e7eb;
  overflow: hidden;
}

.upload-progress-bar-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #4f6ef2, #7b9eff);
  transition: width 0.2s ease;
}

.upload-progress-bar-fill.done { background: #22c55e; }
.upload-progress-bar-fill.error { background: #ef4444; }

.upload-done-preview {
  margin-top: 8px;
  border-radius: 6px;
  overflow: hidden;
}

.done-preview-media {
  width: 100%;
  max-height: 160px;
  object-fit: cover;
  border-radius: 6px;
  display: block;
}

.table-wrap {
  border: 1px solid #e4e9ef;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

table {
  width: 100%;
  border-collapse: collapse;
}

thead {
  background: #f2f6fc;
}

th,
td {
  border-bottom: 1px solid #edf1f5;
  text-align: left;
  padding: 11px 12px;
  font-size: 14px;
}

tbody tr:hover {
  background: #f8fbff;
}

.strong {
  font-weight: 600;
  color: #1f2d3d;
}

.btn {
  border: none;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  transition: transform 0.16s ease, box-shadow 0.16s ease, opacity 0.16s ease;
}

.btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 14px rgba(37, 99, 235, 0.2);
}

.btn.secondary {
  background: #6c757d;
}

.btn.small {
  font-size: 12px;
  padding: 6px 10px;
  margin-right: 6px;
}

.btn.danger {
  background: #dc3545;
}

.btn.ghost {
  background: #eef4ff;
  color: #2c5cc5;
}

.empty-state {
  margin-top: 10px;
  border: 1px dashed #c7d5e6;
  border-radius: 10px;
  background: #f8fbff;
  padding: 14px;
  color: #5a6f85;
  text-align: center;
  font-weight: 500;
}

.status-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-good {
  background: #e8f7ef;
  color: #18794e;
}

.status-payment_delay {
  background: #fff4e5;
  color: #9a5b00;
}

.status-due {
  background: #ffe9e9;
  color: #b42318;
}

.status-hold {
  background: #f3f0ff;
  color: #6941c6;
}

@media (max-width: 1200px) {
  .filters-shell {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .schedule-head,
  .schedule-row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .field-wide {
    grid-column: span 2;
  }
}

@media (max-width: 768px) {
  .content {
    margin-left: 200px;
    padding: 16px;
  }

  .section-toggle {
    padding: 14px;
  }

  .section-body {
    padding: 0 14px 14px;
  }

  .grid {
    grid-template-columns: 1fr;
  }

  .filters-shell,
  .distribution-row,
  .distribution-footer,
  .schedule-head,
  .schedule-row {
    grid-template-columns: 1fr;
  }

  .requirement-grid {
    grid-template-columns: 1fr;
  }

  .field-wide {
    grid-column: span 1;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }
}
</style>
